/**
 * Connector Detail Drawer — Slide-out panel with overview, health, allowlist, lifecycle.
 * Phase 4.7 — WQ-165
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  getConnector, triggerHealthCheck, listAllowlist, addToAllowlist,
  removeFromAllowlist, transitionLifecycle,
  type Connector, type ConnectorAllowlist,
} from '../../services/connectorService';

const STATE_COLORS: Record<string, string> = {
  candidate: '#6b7280', testing: '#3b82f6', approved: '#06b6d4',
  active: '#22c55e', suspended: '#eab308', retired: '#dc2626',
};

const TRANSITIONS: Record<string, string[]> = {
  candidate: ['testing', 'retired'],
  testing: ['approved', 'retired'],
  approved: ['active', 'retired'],
  active: ['suspended', 'retired'],
  suspended: ['active', 'retired'],
  retired: ['candidate'],
};

interface Tenant { id: string; name: string; slug: string; }

interface ConnectorDetailDrawerProps {
  connectorId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

const ConnectorDetailDrawer: React.FC<ConnectorDetailDrawerProps> = ({ connectorId, onClose, onUpdated }) => {
  const { authFetch } = useAuth();
  const [connector, setConnector] = useState<Connector | null>(null);
  const [allowlist, setAllowlist] = useState<ConnectorAllowlist[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const [c, al] = await Promise.all([
        getConnector(authFetch, connectorId),
        listAllowlist(authFetch, connectorId),
      ]);
      setConnector(c);
      setAllowlist(al);
      // Fetch tenants for allowlist dropdown
      try {
        const tRes = await authFetch('/api/admin/tenants');
        if (tRes.ok) {
          const tData = await tRes.json();
          setTenants(Array.isArray(tData) ? tData : tData.tenants || []);
        }
      } catch {
        // Non-critical
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connector');
    }
    setLoading(false);
  }, [authFetch, connectorId]);

  useEffect(() => {
    if (connectorId) fetchDetail();
  }, [connectorId, fetchDetail]);

  const handleHealthCheck = async () => {
    if (!connectorId) return;
    setHealthLoading(true);
    try {
      await triggerHealthCheck(authFetch, connectorId);
      await fetchDetail();
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Health check failed');
    }
    setHealthLoading(false);
  };

  const handleTransition = async (newState: string) => {
    if (!connectorId) return;
    try {
      await transitionLifecycle(authFetch, connectorId, newState);
      await fetchDetail();
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transition failed');
    }
  };

  const handleAddTenant = async () => {
    if (!connectorId || !selectedTenant) return;
    try {
      await addToAllowlist(authFetch, connectorId, selectedTenant);
      setSelectedTenant('');
      await fetchDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add tenant');
    }
  };

  const handleRemoveTenant = async (tenantId: string) => {
    if (!connectorId) return;
    try {
      await removeFromAllowlist(authFetch, connectorId, tenantId);
      await fetchDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove tenant');
    }
  };

  const allowedTenantIds = new Set(allowlist.map(a => a.tenant_id));
  const availableTenants = tenants.filter(t => !allowedTenantIds.has(t.id));

  const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 };
  const valueStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500 };
  const sectionStyle: React.CSSProperties = { marginBottom: 20 };
  const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 600, marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid var(--border)' };

  return (
    <Sheet open={!!connectorId} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" style={{ width: 420, maxWidth: '90vw', overflowY: 'auto', zIndex: 1100 }} className="sheet-glass">
        <SheetHeader>
          <SheetTitle>{connector?.name || 'Connector'}</SheetTitle>
          <SheetDescription>{connector?.description || 'Connector details'}</SheetDescription>
        </SheetHeader>

        {loading && !connector && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
        )}

        {error && (
          <div style={{ padding: 8, background: '#fef2f2', color: '#dc2626', borderRadius: 6, marginTop: 12, fontSize: 13 }}>{error}</div>
        )}

        {connector && (
          <div style={{ marginTop: 16 }}>
            {/* Overview */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Overview</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                <div><div style={labelStyle}>Type</div><div style={valueStyle}>{connector.connector_type.replace('_', ' ')}</div></div>
                <div><div style={labelStyle}>State</div><div style={valueStyle}>
                  <span style={{ color: STATE_COLORS[connector.lifecycle_state] || '#888' }}>{connector.lifecycle_state}</span>
                </div></div>
                <div><div style={labelStyle}>Origin</div><div style={valueStyle}>{connector.origin || '—'}</div></div>
                <div><div style={labelStyle}>Country</div><div style={valueStyle}>{connector.origin_country || '—'} {connector.origin_verified && '(verified)'}</div></div>
                <div><div style={labelStyle}>Transport</div><div style={valueStyle}>{connector.transport || '—'}</div></div>
                <div><div style={labelStyle}>Enabled</div><div style={valueStyle}>{connector.enabled ? 'Yes' : 'No'}</div></div>
                <div style={{ gridColumn: '1/-1' }}><div style={labelStyle}>Endpoint</div><div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{connector.endpoint || '—'}</div></div>
                {connector.credentials_ref && (
                  <div style={{ gridColumn: '1/-1' }}><div style={labelStyle}>Credentials Ref</div><div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: 11 }}>{connector.credentials_ref}</div></div>
                )}
              </div>
            </div>

            {/* Health */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Health</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 8 }}>
                <div><div style={labelStyle}>Last Check</div><div style={valueStyle}>{connector.last_health_check ? new Date(connector.last_health_check).toLocaleString() : 'Never'}</div></div>
                <div><div style={labelStyle}>Status</div><div style={valueStyle}>{connector.last_health_status || 'Unknown'}</div></div>
                <div><div style={labelStyle}>Consecutive Failures</div><div style={valueStyle}>{connector.consecutive_failures}</div></div>
                <div><div style={labelStyle}>Auto-suspend</div><div style={valueStyle}>{connector.auto_suspend_on_failure ? `After ${connector.max_consecutive_failures} failures` : 'Disabled'}</div></div>
                <div><div style={labelStyle}>Kill Switch</div><div style={valueStyle}>{connector.kill_switch_enabled ? 'Enabled' : 'Disabled'}</div></div>
              </div>
              {connector.health_url && (
                <button onClick={handleHealthCheck} style={{
                  fontSize: 12, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }} disabled={healthLoading}>
                  {healthLoading ? 'Checking...' : 'Run Health Check'}
                </button>
              )}
              {!connector.health_url && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No health URL configured</div>}
            </div>

            {/* Invocation Stats */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Invocation Stats</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                <div><div style={labelStyle}>Total Invocations</div><div style={valueStyle}>{connector.total_invocations.toLocaleString()}</div></div>
                <div><div style={labelStyle}>Last Invoked</div><div style={valueStyle}>{connector.last_invoked_at ? new Date(connector.last_invoked_at).toLocaleString() : 'Never'}</div></div>
              </div>
            </div>

            {/* Allowlist */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Access Control (Allowlist)</div>
              {allowlist.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>No tenants in allowlist — connector is available to all.</div>
              ) : (
                <div style={{ marginBottom: 8 }}>
                  {allowlist.map(a => {
                    const tenant = tenants.find(t => t.id === a.tenant_id);
                    return (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ fontSize: 13 }}>{tenant?.name || a.tenant_id}</span>
                        <button onClick={() => handleRemoveTenant(a.tenant_id)}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc262640', background: '#dc262610', color: '#dc2626', cursor: 'pointer' }}>
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {availableTenants.length > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12 }}>
                    <option value="">Select tenant...</option>
                    {availableTenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
                  </select>
                  <button onClick={handleAddTenant} style={{
                    fontSize: 12, padding: '6px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  }} disabled={!selectedTenant}>Add</button>
                </div>
              )}
            </div>

            {/* Lifecycle Actions */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Lifecycle Actions</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(TRANSITIONS[connector.lifecycle_state] || []).map(ns => (
                  <button key={ns} onClick={() => handleTransition(ns)}
                    style={{
                      fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${STATE_COLORS[ns] || '#888'}60`,
                      background: `${STATE_COLORS[ns] || '#888'}15`,
                      color: STATE_COLORS[ns] || '#888', fontWeight: 600,
                    }}>
                    {connector.lifecycle_state === 'retired' && ns === 'candidate' ? 'Re-register' : `Transition to ${ns}`}
                  </button>
                ))}
                {(TRANSITIONS[connector.lifecycle_state] || []).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No transitions available from "{connector.lifecycle_state}"</div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ConnectorDetailDrawer;
